import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, Spinner } from 'ui-component-library';

import MainContainer, {
  TrackingQueueContainer,
  HeaderContainer,
  SelectedOfficesContainer,
  LoadingContainer,
  ErrorContainer,
} from './tracking-queue.styles';
import TrackingQueueFilters from '../../core/common/tracking-queue-filters';
import TrackingQueueTable from '../../core/common/tracking-queue-table/tracking-queue-table';
import TrackingQueuePeekLoad from '../tracking-queue-peek-load';
import TrackingQueueSearchBar from '../../core/common/tracking-queue-search-bar';
import SmartFilters from '../../core/common/smart-filters';
import { setSelectedLoad, clearSelectedLoad } from '../../../store/selected-load/slice';
import {
  getFilterParams,
  updateQueryString,
  getFilterStateFromQueryParams,
} from '../../../utils/mappers';
import OfficePreferences from '../../core/common/office-preferences';
import { STATUS_TYPES } from '../../../constants';

/**
 * Tracking queue container component
 * @return {element}
 */
const TrackingQueue = () => {
  // General Hooks
  const history = useHistory();
  const location = useLocation();

  // Search param derived value
  let searchParams = new URLSearchParams(location.search);

  // Redux hooks
  const dispatch = useDispatch();
  const selectedLoad = useSelector((state) => state.selectedLoad);
  const profileSettings = useSelector((state) => state.profileSettings);
  const metadata = useSelector((state) => state.metadata);
  const modeType = useSelector((state) => state.modeType?.type);

  // State hooks
  const [currentSearch, setCurrentSearch] = useState(searchParams.get('searchTerm') || '');
  const [filterOptions, setFilterOptions] = useState({});
  const [activeFilters, setActiveFilters] = useState(getFilterStateFromQueryParams(searchParams));
  const [smartFilterCounts, setSmartFilterCounts] = useState({});
  const [showOfficePreferencesModal, setShowOfficePreferencesModal] = useState(true);
  /**
   * Handles when a user wants to peek a particular load
   * @param {string} id - ID of the load that was selected
   * @param {string} salesOrderBk - The salesOrderBk of the load that was selected
   * @return {void}
   */
  const onPeekLoad = (id, salesOrderBk) => {
    if (id !== selectedLoad.id) {
      dispatch(setSelectedLoad({
        id,
        salesOrderBk,
      }));
    }
  };
  /**
   * Closes peek view
   * @return {void}
   */
  const closePeekView = () => {
    dispatch(clearSelectedLoad());
  };

  /**
   * Handles when a search term changes
   * @param {string} term - Search term string
   * @return {void}
   */
  const handleSearchTermChanged = (term) => {
    if (currentSearch !== term) {
      setCurrentSearch(term);
      history.replace(`?${updateQueryString(searchParams, { searchTerm: term })}`);
    }
  };

  /**
   * Handles when a smart filter changes
   * @param {string} fieldId - ID of the filter field
   * @return {void}
   */
  const handleUpdateSmartFilters = (fieldId) => {
    if (activeFilters.smartFilter !== fieldId) {
      const newActiveFilters = {
        ...activeFilters,
        smartFilter: fieldId,
      };
      setActiveFilters(newActiveFilters);
      history.replace(`?${updateQueryString(searchParams, { smartFilter: fieldId })}`);
    }
  };

  /**
   * Handles when a filter value changes
   * @param {string} fieldId - ID of the filter field
   * @param {string} filterValue - value of the filter field
   * @return {void}
   */
  const handleUpdateFilters = (fieldId, filterValue) => {
    if (activeFilters[fieldId] !== filterValue) {
      const newActiveFilters = {
        ...activeFilters,
        [fieldId]: filterValue,
      };
      setActiveFilters(newActiveFilters);
      const filterParams = getFilterParams(newActiveFilters);
      history.replace(`?${updateQueryString(searchParams, { [fieldId]: filterParams[fieldId] })}`);
    }
  };

  /**
   * Handles clearing the current filters on the table
   * @return {void}
   */
  const handleClearFilters = () => {
    setActiveFilters({
      smartFilter: activeFilters.smartFilter,
      modeType,
    });
    searchParams = new URLSearchParams();
    history.replace(`?${updateQueryString(searchParams, { searchTerm: currentSearch, smartFilter: activeFilters.smartFilter, modeType })}`);
  };

  /**
   * Handling updating filter options
   * @param {object[]} newFilterOptions - List of new filter options
   * @return {void} - Handles updating filter options
   */
  const handleUpdatedFilterOptions = (newFilterOptions) => {
    setFilterOptions(newFilterOptions);
  };

  /**
   * Handles updating the smart filter counts changing.
   * If new problems is present, it will append new problems
   * to the 'problems' filter option in the UI.
   * @param {object} newCounts - List of new counts
   * @param {string[]} newProblems - List of new problems
   */
  const handleSmartFilterCountsChanged = (newCounts, newProblems) => {
    setSmartFilterCounts({ ...smartFilterCounts, ...newCounts });
    // Check if there are new problems
    if (newProblems) {
      const clone = { ...filterOptions };
      // Combine new problems with existing problems
      // then filter to make sure they are unique.
      clone.Problems = [...new Set([...clone.Problems, ...newProblems])];
      setFilterOptions(clone);
    }
  };

  /**
   * Handles when the sort model changes
   * @param {string} params - AG Grid API params
   * @return {void}
   */
  const handleSortChanged = (params) => {
    const sortModel = params.columnApi.getColumnState();
    const sortBy = sortModel?.length > 0 ? sortModel[0].colId : null;
    const sortDir = sortModel?.length > 0 ? sortModel[0].sort : null;
    const cleanedSortBy = sortBy?.replace('_1', '');
    history.replace(`?${updateQueryString(searchParams, { sortBy: cleanedSortBy, sortDir })}`);
  };

  useEffect(() => {
    if (activeFilters.modeType !== modeType) {
      const { smartFilter } = activeFilters;
      setActiveFilters({ modeType, smartFilter });
    }
  }, [modeType, activeFilters]);

  const noOffices = !profileSettings?.data?.officeNames || profileSettings?.data?.officeNames.length === 0;

  return (
    <MainContainer>
      {metadata?.data?.offices && profileSettings?.data && (
        <Modal
          isOpen={noOffices && showOfficePreferencesModal}
          width="500px"
          title="Office Settings"
          headerContent={<div />}
        >
          <OfficePreferences
            title="Welcome to Track & Trace!"
            description="Before getting started, select which office’s loads to show in your Tracking Queue. You must select at least one office. In the future, this can be updated under your Profile Settings."
            isModal
            closeModal={() => setShowOfficePreferencesModal(false)}
          />
        </Modal>
      )}
      {profileSettings.status === STATUS_TYPES.SUCCEED_STATE && (
        <>
          <SelectedOfficesContainer>
            {profileSettings?.data?.officeNames && `Selected Offices: ${profileSettings?.data?.officeNames.map((office) => ((office).match(/\((.*)\)/).pop())).join(', ')}`}
          </SelectedOfficesContainer>
          <HeaderContainer>
            <SmartFilters
              initialFilter={activeFilters.smartFilter}
              updateFilters={handleUpdateSmartFilters}
              smartFilterCounts={smartFilterCounts}
            />
            <TrackingQueueSearchBar
              searchTermChanged={handleSearchTermChanged}
              currentSearchTerm={currentSearch}
            />
          </HeaderContainer>
          <TrackingQueueFilters
            filterOptions={filterOptions}
            activeFilters={activeFilters}
            handleUpdateFilters={handleUpdateFilters}
            handleClearFilters={handleClearFilters}
            mode={modeType}
          />
          <TrackingQueueContainer>
            <TrackingQueueTable
              onPeekLoad={onPeekLoad}
              search={currentSearch}
              onUpdatedFilters={handleUpdatedFilterOptions}
              filters={activeFilters}
              initialSort={[{ colId: searchParams.get('sortBy'), sort: searchParams.get('sortDir') }]}
              onSortChange={handleSortChanged}
              onSmartFilterCountsChange={handleSmartFilterCountsChanged}
              smartFilterCounts={smartFilterCounts}
              mode={modeType}
            />
            {selectedLoad?.id && selectedLoad?.salesOrderBk && (
              <TrackingQueuePeekLoad
                closePeek={closePeekView}
                loadId={selectedLoad.id}
                salesOrderBk={selectedLoad.salesOrderBk}
                mode={modeType}
              />
            )}
          </TrackingQueueContainer>
        </>
      )}
      {profileSettings.status === STATUS_TYPES.INIT_STATE && (
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      )}
      {profileSettings.status === STATUS_TYPES.FAILED_STATE && (
        <ErrorContainer>
          An unexpected error occured. Unable to fetch profile for current user.
        </ErrorContainer>
      )}
    </MainContainer>
  );
};

export default TrackingQueue;
